import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { taskKeys } from '@/lib/query-keys';
import { taskService } from '@/services/task.service';
import { useTaskFilterStore } from '@/stores/taskFilterStore';

export function useTasks() {
  const queryClient = useQueryClient();
  const filters = useTaskFilterStore();

  const tasksQuery = useQuery({
    queryKey: taskKeys.list(filters.current),
    queryFn: () => taskService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const createTask = useMutation({
    mutationFn: (data: Parameters<typeof taskService.create>[0]) => taskService.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: taskKeys.lists() }); toast.success('Task created'); },
    onError: () => toast.error('Failed to create task'),
  });

  const updateTask = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof taskService.update>[1] }) => taskService.update(id, data),
    onSuccess: (_, { id }) => { queryClient.invalidateQueries({ queryKey: taskKeys.lists() }); queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) }); toast.success('Task updated'); },
    onError: () => toast.error('Failed to update task'),
  });

  const deleteTask = useMutation({
    mutationFn: (id: string) => taskService.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: taskKeys.lists() }); toast.success('Task deleted'); },
    onError: () => toast.error('Failed to delete task'),
  });

  const completeTask = useMutation({
    mutationFn: (id: string) => taskService.complete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: taskKeys.lists() }); toast.success('Task completed'); },
    onError: () => toast.error('Failed to complete task'),
  });

  const bulkComplete = useMutation({
    mutationFn: (ids: string[]) => taskService.bulkComplete(ids),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: taskKeys.lists() }); toast.success('Tasks completed'); },
    onError: () => toast.error('Failed to complete tasks'),
  });

  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => taskService.bulkDelete(ids),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: taskKeys.lists() }); toast.success('Tasks deleted'); },
    onError: () => toast.error('Failed to delete tasks'),
  });

  return {
    tasks: tasksQuery.data?.data ?? [],
    meta: tasksQuery.data?.meta,
    isLoading: tasksQuery.isLoading,
    filters,
    createTask, updateTask, deleteTask, completeTask, bulkComplete, bulkDelete,
  };
}

export function useTask(id: string) {
  const taskQuery = useQuery({ queryKey: taskKeys.detail(id), queryFn: () => taskService.getById(id), enabled: !!id });
  return { task: taskQuery.data?.data, isLoading: taskQuery.isLoading };
}
